import { createContext, useEffect, useState, useCallback } from "react";

// Create the context
export const VersionContext = createContext();

// Version check interval in milliseconds (5 minutes)
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;

export const VersionProvider = ({ children }) => {
  // Initialize with stored version or null
  const [version, setVersion] = useState(localStorage.getItem("appVersion") || null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(
    localStorage.getItem("lastVersionCheck") ?
    parseInt(localStorage.getItem("lastVersionCheck")) : 0
  );
  // Track if we've verified the version already - prevents unnecessary repeated checks
  const [versionVerified, setVersionVerified] = useState(false);

  // Function to check for application updates
  const checkForUpdate = useCallback(async (forceCheck = false) => {
    // Skip checking if DOM isn't fully loaded (helps with custom components like scrollbars)
    if (typeof document === 'undefined' || !document.body) {
      return;
    }

    // Use a unique requestId to deduplicate checks that might be called in quick succession due to StrictMode
    const requestId = Date.now();

    // Skip if already checking, if we've verified the version, or if checked recently (unless forced)
    if (
      isChecking ||
      (!forceCheck && versionVerified) || // Skip if version is already verified
      (!forceCheck && lastChecked && requestId - lastChecked < VERSION_CHECK_INTERVAL)
    ) {
      return;
    }

    // Skip if browser reports no internet connection
    if (!navigator.onLine) {
      return;
    }

    try {
      setIsChecking(true);

      // Use cache-busting query parameter to ensure we get fresh data
      const response = await fetch(`/version.json?t=${requestId}`, {
        cache: "no-store",
        headers: { "Pragma": "no-cache" }
      });

      if (!response.ok) {
        // During deployment, version.json might be temporarily unavailable
        // Instead of throwing error, just postpone the check
        console.log(`Version check temporarily unavailable (${response.status}). Will retry later.`);

        // Don't update lastChecked, so we'll retry sooner
        setIsChecking(false);
        return;
      }

      const data = await response.json();
      const newVersion = data.version;
      const currentVersion = localStorage.getItem("appVersion");

      // Special handling for maintenance mode version
      if (newVersion === "maintenance-mode") {
        console.log("Maintenance mode detected. Postponing version check.");
        // Don't update timestamp or trigger refresh during maintenance
        setIsChecking(false);
        return;
      }

      // Update last check timestamp
      localStorage.setItem("lastVersionCheck", requestId.toString());
      setLastChecked(requestId);
      
          // Completely remove version logging for normal operation
      // console.log(`Current version: ${currentVersion || "None"}`);
      // console.log(`Fetched version: ${newVersion}`);

      // If no current version or version has changed
      if (!currentVersion || newVersion !== currentVersion) {
        console.log("🔄 New version detected. Preparing to update...");

        // Store the new version immediately
        localStorage.setItem("appVersion", newVersion);
        setVersion(newVersion);

        // Clear all per-page version keys so every page will refresh on next visit
        // Also clear any route-specific cache flags
        const cacheKeys = Object.keys(localStorage).filter(key =>
          key.startsWith('page_v_') || key.startsWith('route_cache_') || key.startsWith('component_cache_')
        );
        cacheKeys.forEach(key => localStorage.removeItem(key));

        // Set a timer for page refresh (5 seconds)
        const REFRESH_DELAY = 5000; // 5 seconds
        console.log(`Page will refresh automatically in ${REFRESH_DELAY/1000} seconds`);

        // Wait for DOM operations to complete, then create notification
        setTimeout(() => {
          try {
            // Create notification element using a portal container to avoid DOM conflicts
            const portalContainer = document.getElementById('version-notification-portal') ||
              (() => {
                const container = document.createElement('div');
                container.id = 'version-notification-portal';
                document.body.appendChild(container);
                return container;
              })();

            const notificationDiv = document.createElement('div');
            notificationDiv.style.position = 'fixed';
            notificationDiv.style.top = '20px';
            notificationDiv.style.left = '50%';
            notificationDiv.style.transform = 'translateX(-50%)';
            notificationDiv.style.backgroundColor = '#4a5568';
            notificationDiv.style.color = 'white';
            notificationDiv.style.padding = '12px 24px';
            notificationDiv.style.borderRadius = '4px';
            notificationDiv.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            notificationDiv.style.zIndex = '9999';
            notificationDiv.style.transition = 'opacity 0.5s ease-in-out';
            notificationDiv.textContent = 'New version available. Page will refresh in 5 seconds...';

            // Add to portal container
            portalContainer.appendChild(notificationDiv);

            // Start refresh timer
            setTimeout(() => {
              // Fade out notification
              notificationDiv.style.opacity = '0';

              // Remove notification after fade and refresh page
              setTimeout(() => {
                try {
                  if (portalContainer.contains(notificationDiv)) {
                    portalContainer.removeChild(notificationDiv);
                  }
                } catch (err) {
                  // Ignore any DOM errors
                }
                // Force a hard refresh that clears all caches and ensures all routes are updated
                // Clear service worker cache if present
                if ('serviceWorker' in navigator) {
                  navigator.serviceWorker.getRegistrations().then(registrations => {
                    registrations.forEach(registration => {
                      registration.unregister();
                    });
                  }).catch(() => {});
                }

                // Clear browser caches
                if ('caches' in window) {
                  caches.keys().then(cacheNames => {
                    return Promise.all(
                      cacheNames.map(cacheName => caches.delete(cacheName))
                    );
                  }).catch(() => {});
                }

                // Force reload of current page (not redirect to root) with cache busting
                const currentPath = window.location.pathname;
                const currentSearch = window.location.search;
                const separator = currentSearch ? '&' : '?';
                window.location.href = `${window.location.origin}${currentPath}${currentSearch}${separator}v=${newVersion}&t=${Date.now()}`;
              }, 500); // Wait for fade out
            }, REFRESH_DELAY - 100); // Account for the 100ms delay in creating notification
          } catch (err) {
            // If there's any error with the notification, just reload
            console.error("Error creating notification:", err);
            setTimeout(() => {
              // Same aggressive cache clearing as above
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(registrations => {
                  registrations.forEach(registration => {
                    registration.unregister();
                  });
                }).catch(() => {});
              }

              if ('caches' in window) {
                caches.keys().then(cacheNames => {
                  return Promise.all(
                    cacheNames.map(cacheName => caches.delete(cacheName))
                  );
                }).catch(() => {});
              }

              // Force reload of current page (not redirect to root) with cache busting
              const currentPath = window.location.pathname;
              const currentSearch = window.location.search;
              const separator = currentSearch ? '&' : '?';
              window.location.href = `${window.location.origin}${currentPath}${currentSearch}${separator}v=${newVersion}&t=${Date.now()}`;
            }, REFRESH_DELAY);
          }
        }, 100); // Small delay to let current rendering complete
      } else {
        // If version hasn't changed, update state and mark as verified
        setVersion(newVersion);
        setVersionVerified(true); // Mark version as verified to prevent unnecessary checks
      }
    } catch (error) {
      // Silently ignore network errors (no internet connection, fetch failed, etc.)
      // These are expected when offline and don't need to be logged
      const isNetworkError = error.name === 'TypeError' && error.message.includes('fetch') ||
                            error.message?.includes('network') ||
                            error.message?.includes('internet') ||
                            !navigator.onLine;
      if (!isNetworkError) {
        console.error("❌ Error checking for updates:", error);
      }
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, lastChecked, versionVerified]);

  // Initial app load setup
  useEffect(() => {
    // Clear any stale caches on app load to ensure fresh content
    const clearStaleCaches = async () => {
      try {
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          // Clear caches that might contain old route data
          const staleCacheNames = cacheNames.filter(name => 
            name.includes('static') || name.includes('runtime') || name.includes('chunk')
          );
          await Promise.all(staleCacheNames.map(name => caches.delete(name)));
        }
      } catch (err) {
        // Ignore cache clearing errors
      }
    };
    
    // Clear stale caches first, then check for updates
    clearStaleCaches().then(() => {
      checkForUpdate(true);
    });

    // Set up periodic checks - only needed if version isn't verified yet
    let intervalId;
    if (!versionVerified) {
      intervalId = setInterval(() => {
        if (!versionVerified) {
          checkForUpdate();
        }
      }, VERSION_CHECK_INTERVAL);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkForUpdate, versionVerified]);

  return (
    <VersionContext.Provider value={{ version, checkForUpdate }}>
      {children}
    </VersionContext.Provider>
  );
};